import React, { Component, Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom'
import { route } from "./routes";
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import axios from "axios";
import config from "./config";
import qs from 'qs';
import i18n from './i18n';
import debounce from 'lodash.debounce';
import {withStyleguide, withError, withRouter, Loading, GelContentContainer, NotFound} from "cw-styleguide-react";
import OptionsContext from './contexts/options'
import SearchController from "./controllers/SearchController";
import ResultsController from "./controllers/ResultsController";
import DetailController from "./controllers/DetailController";

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      options: {
        sectors: [],
        hours: [],
        proximity: [],
        apprenticeshipLevels: [],
        welshLanguage: [],
        disabilityConfident: []
      },
      view: 'LIST',
      sort: 'closingDateAsc',
      apprenticeships: [],
      isMobile: false,
      isLoadingApprenticeships: false,
      isLoadingOptions: false,
      page: 1,
      vacancyId: '',
      keyword: '',
      sector: [],
      location: '',
      proximity: '4',
      hours: '',
      level: [],
      welsh: [],
      disability: [],
      query: '',
      total: 0,
      groupSize: 10
    };

    this.loadOptions = this.loadOptions.bind(this);
    this.loadApprenticeships = this.loadApprenticeships.bind(this);
    this.loadApprenticeshipsDebounced = debounce(this.loadApprenticeshipsDebounced.bind(this), 2000);
    this.setApprenticeships = this.setApprenticeships.bind(this);
    this.setView = this.setView.bind(this);
    this.setGroupSize = this.setGroupSize.bind(this);
    this.setPage = this.setPage.bind(this);
    this.setSort = this.setSort.bind(this);
    this.setFilter = this.setFilter.bind(this);
    this.addFilter = this.addFilter.bind(this);
    this.removeFilter = this.removeFilter.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
    this.setQuery = this.setQuery.bind(this);
    this.resetQuery = this.resetQuery.bind(this);
    this.calculateViewportSize = debounce(this.calculateViewportSize.bind(this), 250);
  }

  componentDidMount() {
    // Set the translated route for language switching.
    const toggledLangcode = i18n.language === 'en' ? 'cy' : 'en';
    const translatedRoute = route('apprenticeshipSearch', null, toggledLangcode);

    this.props.styleguide.setLanguageUrl(translatedRoute.substr(1));

    this.calculateViewportSize();
    window.addEventListener('resize', this.calculateViewportSize);

    this.loadOptions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.calculateViewportSize);
  }

  async getFilters(id) {
    return axios.get(config.endpoints.filters, {
      params: {
        filters: id
      },
      headers: {
        'Accept-Language': i18n.language
      }
    })
      .catch(error => {
        console.warn(error);
      });
  }

  loadOptions() {
    this.setState({
      isLoadingOptions: true
    });

    Promise.all([
      this.getFilters('apprenticeshipSector'),
      this.getFilters('apprenticeshipHours'),
      this.getFilters('apprenticeshipProximity'),
      this.getFilters('apprenticeshipLevel'),
      this.getFilters('apprenticeshipWelshLanguage'),
      this.getFilters('apprenticeshipDisabilityConfident')
    ]).then(response => {
      this.setState({
        isLoadingOptions: false,
        options: {
          sectors: response[0].data.apprenticeshipSector.map(sector => ({ ...sector, label: sector.name })),
          hours: response[1].data.apprenticeshipHours.map(hours => ({ ...hours, label: hours.name })),
          proximity: response[2].data.apprenticeshipProximity.map(proximity => ({ ...proximity, label: proximity.name })),
          apprenticeshipLevels: response[3].data.apprenticeshipLevel.map(level => ({ ...level, label: level.name })),
          welshLanguage: response[4].data.apprenticeshipWelshLanguage.map(language => ({ ...language, label: language.name })),
          disabilityConfident: response[5].data.apprenticeshipDisabilityConfident.map(disability => ({ ...disability, label: disability.name }))
        }
      });
    });
  }

  loadApprenticeships(filters = null, page = 1, order = 'closingDateAsc', groupSize = 10) {
    this.setState({
      isLoadingApprenticeships: true
    });

    let params = '';
    if (filters) {
      params = this.convertFiltersToParameters(filters);
    }

    axios.get(`${config.endpoints.q}${params}`, {
      headers: {
        'Accept-Language': i18n.language,
        'page': page,
        'order': order,
        'groupSize': groupSize
      }
    })
      .then(response => {
        this.setApprenticeships(response.data?.data || []);
        this.setState({
          isLoadingApprenticeships: false,
          total: response.data?.results || 0
        });
      })
      .catch(error => {
        // If there is a 400 response then set empty array to prevent error.
        this.setApprenticeships([]);
        this.setState({
          isLoadingApprenticeships: false
        });
      });
  }

  loadApprenticeshipsDebounced(filters = null) {
    this.loadApprenticeships(filters);
  }

  convertFiltersToParameters(filters) {
    let params = {
      id: filters.vacancyId,
      keywords: filters.keyword,
      location: filters.location,
      apprenticeshipSector: filters.sector,
      apprenticeshipLevel: filters.level,
      apprenticeshipProximity: filters.proximity,
      apprenticeshipHours: filters.hours,
      apprenticeshipWelshLanguage: filters.welsh,
      apprenticeshipDisabilityConfident: filters.disability
    };

    // Remove empty filter parameters.
    Object.keys(params).forEach(key => {
      if (!params[key] || params[key].length === 0) {
        delete params[key];
      }
    });

    return qs.stringify(params, {
      addQueryPrefix: true,
      arrayFormat: 'repeat'
    });
  }

  convertParametersToFilters(parameters) {
    const params = qs.parse(parameters, { ignoreQueryPrefix: true });

    let filters = {
      vacancyId: params.id,
      keyword: params.keywords,
      location: params.location,
      sector: params.apprenticeshipSector ? (Array.isArray(params.apprenticeshipSector) ? params.apprenticeshipSector : [params.apprenticeshipSector]) : [],
      level: params.apprenticeshipLevel ? (Array.isArray(params.apprenticeshipLevel) ? params.apprenticeshipLevel : [params.apprenticeshipLevel]) : [],
      proximity: params.apprenticeshipProximity,
      hours: params.apprenticeshipHours,
      welsh: params.apprenticeshipWelshLanguage ? (Array.isArray(params.apprenticeshipWelshLanguage) ? params.apprenticeshipWelshLanguage : [params.apprenticeshipWelshLanguage]) : [],
      disability: params.apprenticeshipDisabilityConfident ? (Array.isArray(params.apprenticeshipDisabilityConfidentv) ? params.apprenticeshipDisabilityConfident : [params.apprenticeshipDisabilityConfident]) : []
    };

    // Remove empty parameter filters.
    Object.keys(filters).forEach(key => {
      if (!filters[key] || filters[key].length === 0) {
        delete filters[key];
      }
    });

    return filters;
  }

  setApprenticeships(apprenticeships) {
    this.setState({
      apprenticeships: apprenticeships
    });
  }

  setView(mode) {
    this.setState({
      view: mode
    });
  }

  setGroupSize(number) {
    this.setState({
      groupSize: number
    });
  }

  setPage(number) {
    this.setState({
      page: number
    });
  }

  setSort(order) {
    this.setState({
      sort: order
    });
  }

  setFilter(filter, value) {
    this.setState({
      [filter]: value
    });
  }

  addFilter(filter, value) {
    // slice() required see https://github.com/facebook/react/issues/2914.
    let values = this.state[filter].slice();
    values.push(value);

    this.setState({
      [filter]: values
    });
  }

  removeFilter(filter, value) {
    let values = this.state[filter];
    values = values.filter(v => v !== value);

    this.setState({
      [filter]: values
    });
  }

  resetFilters() {
    this.setState({
      vacancyId: '',
      keyword: '',
      sector: [],
      location: '',
      proximity: '4',
      hours: '',
      level: [],
      welsh: [],
      disability: []
    });
  }

  setQuery(value) {
    this.setState({
      query: value
    });
  }

  resetQuery() {
    this.setState({
      query: ''
    });
  }

  calculateViewportSize() {
    this.setState({
      isMobile: window.innerWidth < 768
    });
  }

  render() {
    const { options, view, sort, isMobile, isLoadingApprenticeships, apprenticeships, page, query, total, groupSize, ...filters } = this.state;

    if (this.state.isLoadingOptions) {
      return <Loading />;
    }

    return (
      <Fragment>
        <BreadcrumbsItem to={route('apprenticeshipSearch')}>
          {this.props.t('breadcrumbs.apprenticeshipSearch')}
        </BreadcrumbsItem>
        <OptionsContext.Provider value={options}>
          <Routes>
            <Route
              path={route('apprenticeshipSearch')}
              exact={true}
              element={
                <SearchController
                  {...this.props}
                  setFilter={this.setFilter}
                  addFilter={this.addFilter}
                  resetFilters={this.resetFilters}
                  filters={filters}
                  page={page}
                  loadApprenticeships={this.loadApprenticeships}
                  setQuery={this.setQuery}
                  convertFiltersToParameters={this.convertFiltersToParameters}
                />
              }
            />
            <Route
              path={route('results')}
              exact={true}
              element={
                <ResultsController
                  {...this.props}
                  view={view}
                  setView={this.setView}
                  setSort={this.setSort}
                  sort={sort}
                  page={page}
                  apprenticeships={apprenticeships}
                  isLoadingApprenticeships={isLoadingApprenticeships}
                  setApprenticeships={this.setApprenticeships}
                  filters={filters}
                  setGroupSize={this.setGroupSize}
                  setPage={this.setPage}
                  setFilter={this.setFilter}
                  addFilter={this.addFilter}
                  removeFilter={this.removeFilter}
                  isMobile={isMobile}
                  resetFilters={this.resetFilters}
                  loadApprenticeships={this.loadApprenticeships}
                  query={query}
                  setQuery={this.setQuery}
                  resetQuery={this.resetQuery}
                  convertParametersToFilters={this.convertParametersToFilters}
                  convertFiltersToParameters={this.convertFiltersToParameters}
                  total={total}
                  groupSize={groupSize}
                />
              }
            />
            <Route
              path={route('detail')}
              element={
                <DetailController
                  {...this.props}
                  query={query}
                />
              }
            />
            <Route path="*" element={<GelContentContainer><NotFound/></GelContentContainer>} />
          </Routes>
        </OptionsContext.Provider>
      </Fragment>
    );
  }
}

export default withError(withRouter(withStyleguide(withTranslation('local')(App))));
